/**
 * Monthly Check-In Service
 * Handles check-in eligibility, submission, and retrieval
 */
import { supabase } from '../supabase';
import { recordWeighIn, weighIns, newRequestId } from './weightService';
import * as Sentry from '@sentry/react';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// A check-in is offered after this many FULLY completed weeks since the last one...
export const CHECK_IN_REQUIRED_WEEKS = 4;
// ...or, when weeks were interrupted, once this many days have elapsed since the last one
// (with at least one full week banked). Without this floor a single missed day defers the
// check-in indefinitely - see the comment in checkEligibilityForCheckIn.
export const CHECK_IN_INTERVAL_DAYS = 28;

/**
 * Check if user is eligible for monthly check-in
 * Criteria: 4 fully completed weeks since the last check-in, OR at least one full week plus
 * CHECK_IN_INTERVAL_DAYS elapsed (so one interrupted week cannot defer it indefinitely).
 * @param {string} userId - User ID
 * @param {string} programId - Current program ID
 * @returns {Object} { eligible, weeksCompleted, nextCheckInWeek, sessionsCompleted, daysSinceAnchor }
 */
/**
 * What the Monthly Check-In banner says. The check-in can be offered for two reasons: four full weeks, or a
 * month since the last one (with at least one full week banked). Saying "you've completed 2 weeks" when the
 * month was the reason read as a mistake on week 3 of a new program (owner, 30 October 2026).
 */
export const checkInBannerText = (eligibility) =>
  (eligibility?.weeksCompleted ?? 0) >= CHECK_IN_REQUIRED_WEEKS
    ? `You've completed ${eligibility.weeksCompleted} weeks - Time to track your progress`
    : "It's been a month since your last check-in - Time to track your progress";

export const checkEligibilityForCheckIn = async (userId, programId) => {
  try {
    // Get last check-in
    const { data: lastCheckIn } = await supabase
      .from('monthly_check_ins')
      .select('check_in_date, week_number')
      .eq('user_id', userId)
      .order('check_in_date', { ascending: false })
      .limit(1)
      .maybeSingle();

    const lastCheckInDate = lastCheckIn?.check_in_date
      ? new Date(lastCheckIn.check_in_date)
      : null;

    // Get all workout sessions since last check-in (or all time if no check-in)
    // ONLY from the current program
    let query = supabase
      .from('workout_sessions')
      .select('id, started_at, completed_at, day_id, program_id, program_days(week_id, program_weeks(week_number))')
      .eq('user_id', userId)
      .eq('program_id', programId)  // Filter by current program only
      .order('started_at', { ascending: true });

    if (lastCheckInDate) {
      query = query.gt('started_at', lastCheckInDate.toISOString());
    }

    const { data: rawSessions, error } = await query;

    if (error) throw error;

    // Filter to sessions with completed logs OR completed_at
    let sessions = rawSessions || [];
    if (sessions.length > 0) {
      const sessionIds = sessions.map(s => s.id);
      const { data: completedLogs } = await supabase
        .from('workout_logs')
        .select('session_id')
        .in('session_id', sessionIds)
        .eq('completed', true);
      const activeIds = new Set(completedLogs?.map(l => l.session_id) || []);
      sessions = sessions.filter(s => activeIds.has(s.id) || s.completed_at);
    }

    if (!sessions || sessions.length === 0) {
      return {
        eligible: false,
        weeksCompleted: 0,
        nextCheckInWeek: lastCheckIn ? lastCheckIn.week_number + 1 : 1,
        sessionsCompleted: 0,
      };
    }

    // Fetch the full program structure to know how many days each week has
    const { data: programWeeks } = await supabase
      .from('program_weeks')
      .select('week_number, program_days(id)')
      .eq('program_id', programId)
      .order('week_number', { ascending: true });

    // Build map: week_number -> total day count
    const totalDaysPerWeek = {};
    for (const week of programWeeks || []) {
      totalDaysPerWeek[week.week_number] = week.program_days?.length || 0;
    }

    // Track which days have been completed per week
    const completedDaysByWeek = {};
    sessions.forEach(session => {
      const weekNumber = session.program_days?.program_weeks?.week_number;
      const dayId = session.day_id;
      if (weekNumber && dayId) {
        if (!completedDaysByWeek[weekNumber]) completedDaysByWeek[weekNumber] = new Set();
        completedDaysByWeek[weekNumber].add(dayId);
      }
    });

    // Only count a week as completed if ALL its days have been done
    let weeksCompleted = 0;
    for (const [weekNum, totalDays] of Object.entries(totalDaysPerWeek)) {
      const doneDays = completedDaysByWeek[weekNum];
      if (doneDays && totalDays > 0 && doneDays.size >= totalDays) {
        weeksCompleted++;
      }
    }

    // A week only counts when every one of its days is done — that guard (13e0898) stopped the
    // banner firing mid-week and is worth keeping. On its own, though, it is too strict: ONE
    // missed day makes a week worth nothing forever, so a single interrupted week pushes the
    // check-in back indefinitely and "every 4 weeks" quietly becomes "every 4 flawless weeks".
    // A real case: check-in on 28 Aug, week 5 done 1/3, then weeks 6-8 done perfectly — a full
    // month later, still 3 and still not eligible.
    //
    // So there is also a time floor. Once CHECK_IN_INTERVAL_DAYS have passed since the last
    // check-in (or since training began, for a first one) AND at least one full week is banked,
    // they are eligible. That "one full week" keeps both guarantees: someone who has not really
    // trained is never nagged, and a partial week alone is still never enough to trigger it.
    const anchorDate = lastCheckInDate || new Date(sessions[0].started_at);
    const daysSinceAnchor = Math.floor((Date.now() - anchorDate.getTime()) / MS_PER_DAY);
    const eligible =
      weeksCompleted >= CHECK_IN_REQUIRED_WEEKS ||
      (daysSinceAnchor >= CHECK_IN_INTERVAL_DAYS && weeksCompleted >= 1);

    // The week this check-in covers: the furthest week actually trained. The old
    // `lastCheckIn.week_number + 1` drifted out of step the moment a week was missed — after a
    // check-in recorded at week 2, a client on week 9 was offered a check-in labelled week 3,
    // which MonthlyCheckIn renders as "Month 1" (Math.ceil(weekNumber / 4)).
    const trainedWeeks = Object.keys(completedDaysByWeek).map(Number).filter(Number.isFinite);
    const nextCheckInWeek = trainedWeeks.length
      ? Math.max(...trainedWeeks)
      : (lastCheckIn ? lastCheckIn.week_number + 1 : 1);

    return {
      eligible,
      weeksCompleted,
      nextCheckInWeek,
      sessionsCompleted: sessions.length,
      daysSinceAnchor,
    };
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'checkEligibilityForCheckIn', supabaseError: error, userId, programId } });
    console.error('❌ Error checking check-in eligibility:', error);
    return {
      eligible: false,
      weeksCompleted: 0,
      nextCheckInWeek: 1,
      sessionsCompleted: 0,
    };
  }
};

/**
 * Resolve a progress photo path or old public URL to a signed URL.
 * Handles both legacy full URLs and new storage paths.
 * @param {string} pathOrUrl - Storage path (e.g. "userId/file.jpg") or old public URL
 * @param {number} expiresIn - Seconds until expiry (default 1 hour)
 * @returns {string|null} Signed URL or null on error
 */
const normalizePhotoPath = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  let path = pathOrUrl;
  // Handle legacy format: full public URL
  if (pathOrUrl.includes('/progress-photos/')) {
    path = pathOrUrl.split('/progress-photos/')[1];
    path = path.split('?')[0]; // strip query params
  }
  return path;
};

export const getSignedPhotoUrl = async (pathOrUrl, expiresIn = 3600) => {
  const path = normalizePhotoPath(pathOrUrl);
  if (!path) return null;

  try {
    const { data, error } = await supabase.storage
      .from('progress-photos')
      .createSignedUrl(path, expiresIn);

    if (error) {
      console.warn('[getSignedPhotoUrl] signed URL error (non-fatal):', error?.message, path);
      return null;
    }

    return data.signedUrl;
  } catch {
    // Network error on mobile — non-fatal, photos just won't load
    return null;
  }
};

/**
 * Batched version of getSignedPhotoUrl — one Storage API call for many paths
 * instead of one call per photo (the same N+1-shaped issue already fixed for
 * DB queries elsewhere, applied here to Storage). Preserves input order/length,
 * including nulls for empty/invalid entries, so callers can zip results back
 * onto their source array positionally.
 */
export const getSignedPhotoUrls = async (pathsOrUrls, expiresIn = 3600) => {
  const input = pathsOrUrls || [];
  const entries = input
    .map((raw, i) => ({ path: normalizePhotoPath(raw), i }))
    .filter((e) => e.path);

  if (entries.length === 0) return input.map(() => null);

  try {
    const { data, error } = await supabase.storage
      .from('progress-photos')
      .createSignedUrls(entries.map((e) => e.path), expiresIn);

    if (error) {
      console.warn('[getSignedPhotoUrls] batch signed URL error (non-fatal):', error?.message);
      return input.map(() => null);
    }

    const result = input.map(() => null);
    entries.forEach((e, idx) => {
      result[e.i] = data?.[idx]?.signedUrl ?? null;
    });
    return result;
  } catch {
    return input.map(() => null);
  }
};

/**
 * Links to a client's photos for staff (a coach, admin or super admin), made by the server
 * (api/progress-photo-links, migration 096): at most 10 minutes, and never past the end of a coach's
 * coaching, which a link the coach made themself could outlast. Same shape as getSignedPhotoUrls. If the
 * server can't answer, the photo store is asked directly; after 096 it refuses a coach, so a failure shows no
 * photo rather than a link that outlives the coaching.
 * @param {string} clientId - the client's auth id (their photo folder)
 * @param {Array<string|null>} pathsOrUrls
 */
export const getStaffPhotoUrls = async (clientId, pathsOrUrls) => {
  const input = pathsOrUrls || [];
  if (!input.some(Boolean)) return input.map(() => null);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const urls = [];
      for (let i = 0; i < input.length; i += 50) {
        const chunk = input.slice(i, i + 50);
        const res = await fetch('/api/progress-photo-links', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ clientId, paths: chunk }),
        });
        if (!res.ok) throw new Error(`progress-photo-links ${res.status}`);
        const body = await res.json();
        if (!Array.isArray(body?.urls) || body.urls.length !== chunk.length) throw new Error('progress-photo-links: bad answer');
        urls.push(...body.urls);
      }
      return urls;
    }
  } catch (e) {
    console.warn('[getStaffPhotoUrls] server links not made (non-fatal):', e?.message);
  }
  return getSignedPhotoUrls(input);
};

/**
 * Best-effort parse of a photo's front/side/back type from the storage
 * filename convention `{userId}/{timestamp}_{type}.{ext}` baked in by
 * uploadProgressPhoto below. Type isn't a queryable DB column, so this reads
 * it back out of the (possibly signed) URL. Returns null — never a guess —
 * when the filename doesn't match, so callers can fall back to unlabeled
 * display instead of showing a fabricated position.
 * @param {string} url - signed or raw storage URL/path
 * @returns {'front'|'side'|'back'|null}
 */
export const parsePhotoType = (url) => {
  if (!url) return null;
  const pathPart = url.split('?')[0];
  const match = pathPart.match(/_(front|side|back)\.[a-zA-Z0-9]+$/i);
  return match ? match[1].toLowerCase() : null;
};

/**
 * Upload progress photo to Supabase Storage
 * @param {File} file - Image file
 * @param {string} userId - User ID
 * @param {string} photoType - 'front', 'side', or 'back'
 * @returns {string} Storage path of uploaded photo
 */
export const uploadProgressPhoto = async (file, userId, photoType) => {
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Photo must be under 5 MB. Please choose a smaller image.');
  }

  try {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${timestamp}_${photoType}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('progress-photos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    // Return storage path (not public URL — bucket is private)
    return data.path;
  } catch (error) {
    console.error('Error uploading progress photo:', error);
    throw error;
  }
};

/**
 * Submit monthly check-in
 * @param {string} userId - User ID
 * @param {Object} checkInData - Check-in data
 * @returns {Object} Created check-in record
 */
export const submitCheckIn = async (userId, checkInData) => {
  try {
    const { bodyweightKg, photoUrls, messageToCoach, weekNumber, weighedByScan = false } = checkInData;

    const { data, error } = await supabase
      .from('monthly_check_ins')
      .insert({
        user_id: userId,
        bodyweight_kg: bodyweightKg,
        photo_urls: photoUrls || [],
        message_to_coach: messageToCoach || null,
        week_number: weekNumber,
      })
      .select()
      .single();

    if (error) throw error;

    // The check-in's weight is one weigh-in in the one weight record (migration 080), linked to the
    // check-in; the database then sets the current weight in Fitness and Nutrition. Tried twice with
    // the same request id (a retry cannot store it twice). If it still fails, the check-in itself is
    // saved and keeps the weight; this is reported rather than shown as a failed check-in, which the
    // client would then submit again.
    // weighedByScan: a body scan added in this check-in already put today's weight in the record, with
    // the same number; a second weigh-in would only repeat it (Body stats, the check-in's scan step)
    if (bodyweightKg != null && !weighedByScan) {
      const requestId = newRequestId();
      const save = () => recordWeighIn(userId, bodyweightKg, { notes: 'Fitness check-in', checkInId: data.id, requestId });
      try {
        await save().catch(save);
      } catch (weightErr) {
        console.error('[checkInService] check-in weight not added to the weight record:', weightErr);
        Sentry.captureException(weightErr, { extra: { fn: 'submitCheckIn', checkInId: data.id } });
      }
    }

    return data;
  } catch (error) {
    console.error('Error submitting check-in:', error);
    throw error;
  }
};

/**
 * Get all check-ins for a user, including onboarding photos
 * @param {string} userId - User ID
 * @param {{ staff?: boolean }} [options] - staff: a coach or admin looking at a client (links from the server)
 * @returns {Array} Array of check-in records with onboarding photos first
 */
export const getUserCheckIns = async (userId, { staff = false } = {}) => {
  try {
    // Fetch regular check-ins
    const { data: checkIns, error: checkInError } = await supabase
      .from('monthly_check_ins')
      .select('*')
      .eq('user_id', userId)
      .order('check_in_date', { ascending: true }); // Oldest first

    if (checkInError) throw checkInError;

    // Fetch onboarding photos
    const { data: onboardingPhotos, error: photoError } = await supabase
      .from('check_in_photos')
      .select('*')
      .eq('user_id', userId)
      .ilike('notes', '%Onboarding%')
      .order('created_at', { ascending: true });

    if (photoError) {
      Sentry.captureException(photoError, { extra: { fn: 'getUserCheckIns', userId } });
      console.error('[checkInService] check_in_photos fetch failed:', photoError);
    }

    // Helper: resolve an array of paths/URLs to signed URLs — one Storage API
    // call per check-in instead of one call per photo.
    const resolveUrls = (urls) => (staff ? getStaffPhotoUrls(userId, urls) : getSignedPhotoUrls(urls));

    // Combine onboarding photos into a single "onboarding" check-in entry
    if (onboardingPhotos && onboardingPhotos.length > 0) {
      const resolvedOnboardingUrls = await resolveUrls(
        onboardingPhotos.map(p => p.photo_url)
      );
      const onboardingEntry = {
        id: 'onboarding',
        user_id: userId,
        check_in_date: onboardingPhotos[0].created_at,
        bodyweight_kg: null,
        photo_urls: resolvedOnboardingUrls.filter(Boolean),
        message_to_coach: null,
        week_number: 0,
        is_onboarding: true,
      };

      const resolvedCheckIns = await Promise.all(
        (checkIns || []).map(async (c) => ({
          ...c,
          photo_urls: await resolveUrls(c.photo_urls),
        }))
      );

      return [onboardingEntry, ...resolvedCheckIns];
    }

    // No onboarding photos — just resolve check-in URLs
    return Promise.all(
      (checkIns || []).map(async (c) => ({
        ...c,
        photo_urls: await resolveUrls(c.photo_urls),
      }))
    );
  } catch (error) {
    console.error('Error fetching check-ins:', error);
    throw error;
  }
};

/**
 * Weight history for graphing: every weigh-in from both apps (one weight record, migration 080).
 * @param {string} userId - User ID
 * @param {number} months - Number of months to fetch (default 6)
 * @returns {Array} Array of { id, date, weight, source, checkInId } objects, oldest first
 */
export const getWeightHistory = async (userId, months = 6) => {
  try {
    return await weighIns(userId, months);
  } catch (error) {
    console.error('Error fetching weight history:', error);
    throw error;
  }
};

/**
 * Delete a progress photo
 * @param {string} photoUrl - URL of photo to delete
 */
export const deleteProgressPhoto = async (photoUrl) => {
  try {
    // Handle both legacy full URLs and new storage paths
    let filePath = photoUrl;
    if (photoUrl.includes('/progress-photos/')) {
      filePath = photoUrl.split('/progress-photos/')[1].split('?')[0];
    }

    const { error } = await supabase.storage
      .from('progress-photos')
      .remove([filePath]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting progress photo:', error);
    throw error;
  }
};

/**
 * Recent check-ins with no Coach Review yet, counted per client, for the coach's "Needs you today".
 * A check-in is reviewed when a coach_notes row points at it (check_in_id, migration 052).
 * Throws on a failed read: the caller says so rather than showing "nothing waiting".
 * @param {string[]} userIds - clients' auth ids
 * @param {number} sinceDays - how far back a check-in still counts as waiting
 * @returns {Object} counts by user_id
 */
export const getCheckInsAwaitingReview = async (userIds, sinceDays, now = new Date()) => {
  const detail = await getCheckInsAwaitingReviewDetail(userIds, sinceDays, now);
  const counts = {};
  Object.entries(detail).forEach(([uid, d]) => { counts[uid] = d.count; });
  return counts;
};

// The same, with the newest waiting check-in's submission time per client, for "Needs you".
export const getCheckInsAwaitingReviewDetail = async (userIds, sinceDays, now = new Date()) => {
  if (!userIds || userIds.length === 0) return {};
  const since = new Date(now.getTime() - sinceDays * 24 * 60 * 60 * 1000).toISOString();
  const { data: checkIns, error } = await supabase
    .from('monthly_check_ins')
    .select('id, user_id, created_at')
    .in('user_id', userIds)
    .gte('check_in_date', since);
  if (error) throw error;
  if (!checkIns || checkIns.length === 0) return {};

  const { data: reviews, error: reviewsError } = await supabase
    .from('coach_notes')
    .select('check_in_id')
    .in('check_in_id', checkIns.map((c) => c.id));
  if (reviewsError) throw reviewsError;

  const reviewed = new Set((reviews || []).map((r) => r.check_in_id));
  const out = {};
  checkIns.filter((c) => !reviewed.has(c.id)).forEach((c) => {
    const o = out[c.user_id] || (out[c.user_id] = { count: 0, newest: null });
    o.count += 1;
    if (c.created_at && (!o.newest || c.created_at > o.newest)) o.newest = c.created_at;
  });
  return out;
};
